import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useRouter } from 'src/hooks/useRouter';
import { useGetPromotions } from 'src/services/apiService';
import { isAfter, isBefore } from 'src/helpers/date';
import { Promotions } from 'src/components/Promotions';
import { ContractorSelect } from '../ContractorSelect';
import { Contractor } from 'src/shared/api';
import { OutletSelect } from '../OutletSelect';
import { useDebounce } from 'src/hooks/useDebounce';
import { CreatePromotion } from 'src/features/create-promotion';
import { removeEmptyProps } from 'src/helpers/removeEmtyProperties';
import {
  BANNER_DISPLAY_TYPE_MAP,
  BANNER_TYPE_MAP,
} from 'src/entities/promotion';

interface Props {}

export const PromotionsPage: React.FunctionComponent<Props> = ({}) => {
  const { query, ...router } = useRouter();

  const [state, setState] = useState({ page: 0, rowsPerPage: 5 });

  const [vendorId, setVendorId] = useState<null | string>(null);

  const debouncedVendorId = useDebounce(vendorId, 500);

  const [contractor, setContractor] = useState<null | Contractor>(null);

  const variables = removeEmptyProps({
    limit: parseInt(query.limit, 10) || 30,
    offset: parseInt(query.offset, 10) || 0,
    outlets: query.outlets,
    vendorId: query.vendorId,
    withBanners: query.withBanners?.length ? query.withBanners : null,
    bannerType: query.bannerType,
    bannerDisplayType: query.bannerDisplayType,
  });

  useEffect(() => {
    const nextVariables = {
      ...variables,
      vendorId:
        debouncedVendorId !== null && debouncedVendorId !== query.vendorId
          ? debouncedVendorId
          : query.vendorId,
    };
    push(nextVariables);
  }, [debouncedVendorId]);

  useEffect(() => {
    if (vendorId && !query.vendorId?.includes(vendorId))
      setVendorId(query.vendorId);
  }, [query]);

  const request = useGetPromotions({ variables });

  const toggleTimeQuery = (item: string) =>
    push({
      time: query?.time?.includes(item)
        ? query?.time?.filter((i: string) => i !== item)
        : [item],
    });

  const data = request.data?.data?.filter(promotion => {
    const conditions = [];
    if (query?.time?.includes('past')) {
      promotion?.finish &&
        conditions.push(isBefore(new Date(promotion?.finish), new Date()));
    }

    if (query?.time?.includes('future')) {
      promotion?.start &&
        conditions.push(isAfter(new Date(promotion?.start), new Date()));
    }

    if (query?.time?.includes('present')) {
      promotion?.start &&
        promotion?.finish &&
        conditions.push(
          isBefore(new Date(promotion?.start), new Date()) &&
            isAfter(new Date(promotion?.finish), new Date()),
        );
    }

    return conditions.every(item => item);
  });

  const push = ({ page, ...nextQuery }: any) =>{
    const offset = nextQuery.offset ?? variables.offset
    
    router.push(
      null,
      removeEmptyProps({
        ...variables,
        ...nextQuery,
        limit: nextQuery.limit || variables.limit,
        offset: page ? page * variables.limit : offset,
      }),
    );
}
  return (
    <div>
      <Box display="flex" justifyContent="flex-end">
        <CreatePromotion afterCreate={request.refetch} />
      </Box>
      <Box mt={2}>
        <Paper>
          <Box p={2}>
            <Box display="flex" flexWrap="wrap" alignItems="center">
              <Box ml={2}>
                <FormControlLabel
                  label="С баннером"
                  checked={Boolean(query?.withBanners?.length)}
                  onChange={() =>
                    push({
                      withBanners: query?.withBanners?.length ? null : [true],
                    })
                  }
                  control={<Checkbox />}
                />
              </Box>
              <Box ml={2}>
                <FormControl>
                  <InputLabel id="banner-display-type">
                    Место баннера
                  </InputLabel>
                  <Select
                    sx={{ minWidth: 160 }}
                    labelId="banner-display-type"
                    value={query.bannerDisplayType || ''}
                    label="Место баннера"
                    onChange={e => push({ bannerDisplayType: e.target.value, offset: 0 })}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {Object.values(BANNER_DISPLAY_TYPE_MAP)?.map(
                      (item: any) => <MenuItem value={item}>{item}</MenuItem>,
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box ml={2}>
                <FormControl>
                  <InputLabel id="banner-type">Внешний вид баннера</InputLabel>
                  <Select
                    sx={{ minWidth: 220 }}
                    labelId="banner-type"
                    value={query.bannerType || ''}
                    label="Внешний вид баннера"
                    onChange={e => push({ bannerType: e.target.value, offset: 0 })}
                  >
                    <MenuItem value="">Все</MenuItem>
                    {Object.values(BANNER_TYPE_MAP)?.map((item: any) => (
                      <MenuItem value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box mt={2}>
              <ContractorSelect onSelect={setContractor} />
            </Box>
            <Box mt={2}>
              <OutletSelect
                onSelect={outlet => outlet && push({ outlets: outlet.id })}
                outlets={contractor?.outlets}
              />
            </Box>
            <Box mt={2}>
              <TextField
                fullWidth
                label="vendorId"
                value={vendorId}
                onChange={e => setVendorId(e.target.value)}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* <Paper>
        <Box px={2} py={1}>
          <FormControlLabel
            label="Прошедшие"
            checked={query?.time?.includes('past')}
            onChange={() => toggleTimeQuery('past')}
            control={<Checkbox />}
          />
          <FormControlLabel
            label="Текущие"
            checked={query?.time?.includes('present')}
            onChange={() => toggleTimeQuery('present')}
            control={<Checkbox />}
          />
          <FormControlLabel
            label="Будущие"
            checked={query?.time?.includes('future')}
            onChange={() => toggleTimeQuery('future')}
            control={<Checkbox />}
          />
        </Box>
      </Paper> */}
      <Box mt={2}>
        <Promotions
          promotions={data}
          limit={variables.limit}
          offset={variables.offset}
          totalCount={request.data?.totalCount}
          refetch={request.refetch}
          onChange={push}
          isLoading={request.isLoading}
        />
      </Box>
    </div>
  );
};
